import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
  MouseEvent as ReactMouseEvent,
} from "react";
import { allCells, ButtonCellType } from "@glideapps/glide-data-grid-cells";
import { useIntl } from "react-intl";
import DataEditor, {
  Item,
  BooleanCell,
  Rectangle,
  GridSelection,
  ImageCell,
  DataEditorProps,
  GridCell,
  GridCellKind,
  GridColumn,
  SpriteMap,
} from "@glideapps/glide-data-grid";
import DatePicker from "react-datepicker";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import { ReactComponent as RemoveIcon } from "src/images/close-alt.svg";
import { ReactComponent as NavigationIcon } from "src/images/navigation.svg";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import {
  getDictionaryAutoCompletesForColumns,
  getImageSource,
  getSelectDataSetForColumns,
  isThumbnailsExist,
} from "src/utils/methods";
import { IMember } from "src/app/project/ProjectTypes";
import { DictionaryElementSelector } from "src/app/dropdowns/DictionaryElementSelector/DictionaryElementSelector";
import creatorPlaceholder from "src/images/creator-placeholder.svg";
import { RootState } from "src/redux/reducers";
import {
  CurrencySettings,
  MetaDataFileValue,
  Row,
} from "src/redux/dictionary/types";
import useGetGlobalTasksSearchResults from "src/hooks/useGetGlobalTasksSearchResults";
import { DateObject, SelectOption, SortDirection } from "src/types";
import {
  initialHoveredCellSettings,
  isDateObject,
  initialTheme,
  initialDropdownSettings,
  initialTooltipSettings,
  MAX_VISIBLE_MULTI_SELECT_OPTIONS,
  MAX_CHARS_LENGTH,
  generateSpecificGridCell,
  headerIconsObj,
  generateColumns,
  dispatchUpdatedMetaDataValue,
  willElementRenderInsideHoverArea,
  isAccessLevelReadOnly,
  initialHoveredHeaderSettings,
  initialStaticCellSettings,
  getCellPosition,
  rowMarkersSettings,
  areArraysTheSame,
  addNewSelectedRows,
  HEADER_HEIGHT,
  CURRENCY_CODE_COLOR,
  CURRENCY_CODE_INSET,
  CURRENCY_CODE_GAP,
  CURRENCY_VALUE_INSET,
  ROW_HEIGHT,
  FROZEN_COLUMN_COUNT,
  DEFAULT_ROW_MARKER_WIDTH,
  TOTALS_ROW_HEIGHT,
  TOTALS_ROW_EXTRA_LINE_HEIGHT,
} from "./utils";
import { GlideDataGridTooltip } from "./components/GlideDataGridTooltip/GlideDataGridTooltip";
import { ActionDropdownMenu } from "./components/ActionDropdownMenu/ActionDropdownMenu";
import {
  GlideDataGridProps,
  ExtraGlideDataGridMetaFieldTypesEnum,
} from "./types";
import {
  languages,
  dateFormatForBackend,
  datePickerDateFormat,
} from "../CustomDatePicker/utils";
import SelectDropdown from "./components/SelectDropdown/SelectDropdown";
import { GlideDataGridDropdownWrapper } from "./components/GlideDataGridDropdownWrapper/GlideDataGridDropdownWrapper";
import { TextBoxWrapper } from "../TextBoxWrapper/TextBoxWrapper";
import SocialProfileIcons from "../IconsWithStatus/SocialProfileIcons/SocialProfileIcons";
import { setDictionarySort } from "../DictionaryFilters/redux/dictionaryFiltersActions";
import { ValueInputWrapper } from "./components/ValueInputWrapper/ValueInputWrapper";
import MembersDropdown from "./components/MembersDropdown/MembersDropdown";
import { openDictionaryDetails } from "../DictionaryDetails/methods";
import { Img } from "../Img/Img";
import GlideDataGridSelectedRowsBar from "./components/GlideDataGridSelectedRowsBar/GlideDataGridSelectedRowsBar";
import {
  computeSelectedRowsTotals,
  SelectedRowsTotal,
} from "src/app/methods/computeSelectedRowsTotals";
import { tableDataType } from "../Table/Table";
import RatingSelector from "../RatingSelector/RatingSelector";
import { GlideDataGridHeader } from "./components/GlideDataGridHeader/GlideDataGridHeader";
import FilesDropdownWrapper from "./components/FilesDropdownWrapper/FilesDropdownWrapper";
import EmptyCell from "./components/Cells/jsxCells/EmptyCell";
import EmptyFileCell from "./components/Cells/jsxCells/EmptyFileCell";
import StaticFilesLoadingCell from "./components/Cells/jsxCells/StaticFilesLoadingCell";
import creatorCellRenderer, {
  TitleCell,
} from "./components/Cells/canvasCells/TitleCell";
import ProjectSelector from "../ProjectSelector/ProjectSelector";
import GlobalCreatorDropdown from "./components/GlobalCreatorDropdown/GlobalCreatorDropdown";
import AggregatedSelect from "../AggregatedSelect/AggregatedSelect";
import { getCountedSelectValues } from "../AggregatedSelect/components/AggregatedLabels/AggregatedLabels";

import "@glideapps/glide-data-grid/dist/index.css";
import "./GlideDataGrid.scss";

function GlideDataGrid({
  columnsData,
  rowsData,
  isLoading,
  context,
  handleRowNameClick,
  filtersAndSortingOptions,
  filtersAndSortingOptionsDispatch,
  showSearch,
  setShowSearch,
  openGlideElement,
  totalRowsCount,
}: GlideDataGridProps) {
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownSettings, setDropdownSettings] = useState(
    initialDropdownSettings,
  );
  const [hoveredCellSettings, setHoveredCellSettings] = useState(
    initialHoveredCellSettings,
  );
  const [hoveredHeaderSettings, setHoveredHeaderSettings] = useState(
    initialHoveredHeaderSettings,
  );
  const [tooltipSettings, setTooltipSettings] = useState(
    initialTooltipSettings,
  );
  const [staticCellSettings, setStaticCellSettings] = useState(
    initialStaticCellSettings,
  );
  const [headerPopperRef, setHeaderPopperRef] = useState<HTMLElement | null>(
    null,
  );
  const [cellPopperRef, setCellPopperRef] = useState<HTMLElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [selection, setSelection] = useState<GridSelection>();
  const [selectedRowsData, setSelectedRowsData] = useState<Row[]>([]);

  const selectedRowsDataForTotals = useMemo(() => {
    if (!selectedRowsData.length) return [];
    const selectedUuids = new Set(selectedRowsData.map((row) => row.uuid));
    return rowsData.filter((row) => selectedUuids.has(row.uuid));
  }, [selectedRowsData, rowsData]);

  const selectedRowsTotals = useMemo(
    () => computeSelectedRowsTotals(selectedRowsDataForTotals, columnsData),
    [selectedRowsDataForTotals, columnsData],
  );
  const totalsByColumnId = useMemo(() => {
    const map = new Map<string, SelectedRowsTotal[]>();
    selectedRowsTotals.forEach((total) => {
      const existing = map.get(total.uuid);
      if (existing) existing.push(total);
      else map.set(total.uuid, [total]);
    });
    return map;
  }, [selectedRowsTotals]);

  const maxTotalsPerColumn = useMemo(() => {
    let max = 0;
    totalsByColumnId.forEach((totals) => {
      if (totals.length > max) max = totals.length;
    });
    return max;
  }, [totalsByColumnId]);

  const totalsRowHeight =
    maxTotalsPerColumn > 0
      ? TOTALS_ROW_HEIGHT +
        Math.max(0, maxTotalsPerColumn - 1) * TOTALS_ROW_EXTRA_LINE_HEIGHT
      : 0;

  const headerHeight =
    selectedRowsTotals.length > 0
      ? HEADER_HEIGHT + totalsRowHeight
      : HEADER_HEIGHT;

  const [lastClickedRowIndex, setLastClickedRowIndex] = useState<number | null>(
    null,
  );

  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();

  const drawHeader = useCallback<NonNullable<DataEditorProps["drawHeader"]>>(
    (args, drawContent) => {
      if (totalsByColumnId.size === 0) {
        drawContent();
        return;
      }

      const { ctx, rect, column } = args;
      const bandY = rect.y + HEADER_HEIGHT;
      const bandHeight = rect.height - HEADER_HEIGHT;
      if (bandHeight <= 0) {
        drawContent();
        return;
      }

      ctx.save();
      ctx.translate(0, -bandHeight / 2);
      drawContent();
      ctx.restore();

      ctx.save();
      ctx.beginPath();
      ctx.rect(rect.x, bandY, rect.width, bandHeight);
      ctx.clip();

      ctx.fillStyle = initialTheme.bgHeader as string;
      ctx.fillRect(rect.x, bandY, rect.width, bandHeight);

      ctx.strokeStyle = initialTheme.horizontalBorderColor as string;
      ctx.beginPath();
      ctx.moveTo(rect.x, bandY);
      ctx.lineTo(rect.x + rect.width, bandY);
      ctx.stroke();

      const textX = rect.x + (initialTheme.cellHorizontalPadding ?? 20);
      ctx.textBaseline = "middle";

      if (column.id === "title") {
        ctx.font = `${initialTheme.headerFontStyle} ${initialTheme.fontFamily}`;
        ctx.fillStyle = initialTheme.textHeader as string;
        ctx.fillText(
          intl
            .formatMessage({ id: "ws_total", defaultMessage: "Total" })
            .toUpperCase(),
          textX,
          bandY + bandHeight / 2 + 3,
        );
      } else if (column.id) {
        const totals = totalsByColumnId.get(column.id);
        if (totals?.length) {
          ctx.font = `400 13px ${initialTheme.fontFamily}`;
          ctx.fillStyle = initialTheme.textDark as string;
          // one line per distinct currency, stacked and evenly split across
          // the (possibly taller, if another column has more currencies) band
          const lineHeight = bandHeight / totals.length;
          totals.forEach((total, index) => {
            const textY = bandY + lineHeight * index + lineHeight / 2 + 3;
            ctx.fillText(total.formatted, textX, textY);
          });
        }
      }

      ctx.restore();
    },
    [totalsByColumnId, intl],
  );

  // Idle currency cells: grey code + dark value, laid out like the editor that
  // replaces them on hover (see `glide-data-grid__hovered-item--currency`).
  const drawCell = useCallback<NonNullable<DataEditorProps["drawCell"]>>(
    (args, drawContent) => {
      const { ctx, rect, cell, col } = args;
      const columnId = columns[col]?.id;
      const isCurrencyCell =
        cell.kind === GridCellKind.Text &&
        columnsData.find((colData) => colData.uuid === columnId)?.type ===
          MetaFieldType.Currency;

      if (!isCurrencyCell || cell.kind !== GridCellKind.Text) {
        drawContent();
        return;
      }

      const [code, ...valueParts] = cell.displayData.trim().split(" ");
      const value = valueParts.join(" ");
      if (!code || !value) {
        drawContent();
        return;
      }

      ctx.save();
      ctx.beginPath();
      ctx.rect(rect.x, rect.y, rect.width, rect.height);
      ctx.clip();
      ctx.font = `${initialTheme.baseFontStyle} ${initialTheme.fontFamily}`;
      ctx.textBaseline = "middle";

      const textY = rect.y + rect.height / 2 + 1;
      const codeX =
        rect.x +
        (initialTheme.cellHorizontalPadding ?? 20) +
        CURRENCY_CODE_INSET;

      ctx.fillStyle = CURRENCY_CODE_COLOR;
      ctx.fillText(code, codeX, textY);

      ctx.fillStyle = initialTheme.textDark as string;
      ctx.fillText(
        value,
        codeX +
          ctx.measureText(code).width +
          CURRENCY_CODE_GAP +
          CURRENCY_VALUE_INSET,
        textY,
      );
      ctx.restore();
    },
    [columns, columnsData],
  );

  const inputRef = useRef<HTMLInputElement | null>(null);
  const hoveredItemRef = useRef<HTMLDivElement | null>(null);
  const isShiftPressedRef = useRef(false);

  const dictionaryAutoCompletes = useSelector(
    (state: RootState) => state.dictionaryReducer.dictionaryAutoCompletes,
  );

  const locale = useSelector((state: RootState) => state.mainReducer.locale);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const membersList = useSelector(
    (state: RootState) => state.projectReducer.activeMembersList,
  );

  const selectDataSetList = useSelector(
    (state: RootState) => state.selectDataSetReducer.selectDataSetList,
  );

  const [
    globalTasksSearchResultsState,
    initializeGlobalTasksSearchResultsFetch,
  ] = useGetGlobalTasksSearchResults();

  const onHeaderHovered = useCallback(
    (location: Item, bounds: Rectangle) => {
      const [col] = location;

      const hoveredColumn = columns[col];

      setHoveredHeaderSettings(initialHoveredHeaderSettings);
      setTooltipSettings(initialTooltipSettings);

      if (hoveredColumn) {
        setHoveredHeaderSettings({
          value: hoveredColumn.title,
          style: {
            backgroundColor: "white",
            ...getCellPosition({ ...bounds, height: HEADER_HEIGHT }),
          },
          bounds,
          uuid: hoveredColumn.id ?? "",
        });
      }
    },
    [columns],
  );

  const onItemHovered = useCallback(
    (location: Item, bounds: Rectangle) => {
      if (isFocused || isDropdownOpen) return;

      const [col, row] = location;
      setHoveredCellSettings(initialHoveredCellSettings);
      setTooltipSettings(initialTooltipSettings);

      const hoveredColumn = columns[col];

      if (
        hoveredColumn &&
        hoveredColumn.id !== ExtraGlideDataGridMetaFieldTypesEnum.Empty
      ) {
        const cellCoordinates = {
          cell: location,
          bounds,
        };

        const cellPosition = getCellPosition(bounds);

        // This is a hack to display action dropdown
        if (hoveredColumn.id === ExtraGlideDataGridMetaFieldTypesEnum.Action) {
          setHoveredCellSettings({
            type: ExtraGlideDataGridMetaFieldTypesEnum.Action,
            value: "",
            ...cellCoordinates,
            style: {
              backgroundColor: "transparent",
              ...cellPosition,
            },
            readOnly: false,
          });
          return;
        }

        if (hoveredColumn.id === ExtraGlideDataGridMetaFieldTypesEnum.Title) {
          setHoveredCellSettings({
            type: ExtraGlideDataGridMetaFieldTypesEnum.Title,
            value: rowsData[row].title,
            ...cellCoordinates,
            style: {
              backgroundColor: "transparent",
              ...cellPosition,
            },
            readOnly: false,
          });
          return;
        }

        const hoveredColumnData = columnsData.find(
          (colData) => colData.uuid === hoveredColumn.id,
        );

        if (
          !hoveredColumnData ||
          hoveredColumnData?.type === MetaFieldType.BoolVal
        )
          return;

        const hoveredCellMetaData = rowsData[row].metadata.find(
          (m) => m.uuid === hoveredColumn.id,
        );

        const hoveredColumnDataSettings = {
          type: hoveredColumnData.type,
          readOnly:
            isAccessLevelReadOnly(hoveredColumnData.accessLevel) ||
            Boolean(hoveredColumnData.valueSource),
          columnCurrencyCode:
            hoveredColumnData.type === MetaFieldType.Currency
              ? (hoveredColumnData.data as CurrencySettings)?.currencyCode
              : undefined,
          customCurrencyCode:
            hoveredColumnData.type === MetaFieldType.Currency
              ? hoveredCellMetaData?.data?.customCurrencyCode
              : undefined,
          customCurrencyValue:
            hoveredColumnData.type === MetaFieldType.Currency
              ? hoveredCellMetaData?.data?.customCurrencyValue
              : undefined,
        };

        const backgroundColor =
          [
            MetaFieldType.Text,
            MetaFieldType.Number,
            MetaFieldType.Currency,
            MetaFieldType.Percent,
          ].includes(hoveredColumnData.type) || !hoveredCellMetaData?.value
            ? "#fff"
            : "transparent";

        if (
          !hoveredCellMetaData ||
          !hoveredCellMetaData.value ||
          (Array.isArray(hoveredCellMetaData.value) &&
            !hoveredCellMetaData.value.length)
        ) {
          setHoveredCellSettings({
            ...hoveredColumnDataSettings,
            value: null,
            ...cellCoordinates,
            style: {
              backgroundColor,
              ...cellPosition,
            },
          });
          return;
        }

        if (hoveredColumnData.type === MetaFieldType.Text) {
          setHoveredCellSettings({
            ...hoveredColumnDataSettings,
            value: hoveredCellMetaData.value,
            ...cellCoordinates,
            style: {
              backgroundColor,
              ...cellPosition,
            },
          });
          return;
        }

        setHoveredCellSettings({
          ...hoveredColumnDataSettings,
          value: hoveredCellMetaData?.value,
          ...cellCoordinates,
          style: {
            backgroundColor,
            ...cellPosition,
          },
        });

        if (hoveredColumnData.type === MetaFieldType.Member) {
          const foundMember: IMember | undefined = membersList?.find(
            (member) => member.id === hoveredCellMetaData?.value,
          );
          if (!foundMember || foundMember.name.length <= MAX_CHARS_LENGTH) {
            setTooltipSettings(initialTooltipSettings);
          } else {
            setTooltipSettings({
              value: foundMember.name,
              type: hoveredColumnData.type,
              bounds: cellCoordinates.bounds,
            });
          }
        }

        if (hoveredColumnData.type === MetaFieldType.MultiSelect) {
          if (typeof hoveredCellMetaData.value === "string") {
            setTooltipSettings(initialTooltipSettings);
            return;
          }

          const hoveredCellValueArr = hoveredCellMetaData.value as string[];

          const multiSelectSettings = hoveredColumnData.data as {
            singleSelectOptions: SelectOption[];
          };

          const wsSelectMultiDataSetUuid =
            hoveredColumnData.data.wsSelectDataSetUuid ?? null;
          const selectMultiOptions = wsSelectMultiDataSetUuid
            ? selectDataSetList[`dataSetType_${wsSelectMultiDataSetUuid}`]
            : multiSelectSettings.singleSelectOptions;

          const selectedOptions = selectMultiOptions?.filter((option) => {
            return hoveredCellValueArr.find((val) => val === option.value);
          });

          if (selectedOptions?.length >= MAX_VISIBLE_MULTI_SELECT_OPTIONS) {
            setTooltipSettings({
              value: selectedOptions
                .map((item: SelectOption) => item.name)
                .join(", "),
              type: hoveredColumnData.type,
              bounds: cellCoordinates.bounds,
            });
          } else {
            setTooltipSettings(initialTooltipSettings);
          }
        }

        if (hoveredColumnData.type === MetaFieldType.AggregatedSelect) {
          if (typeof hoveredCellMetaData.value === "string") {
            setTooltipSettings(initialTooltipSettings);
            return;
          }

          const hoveredCellValueArr = hoveredCellMetaData.value as string[];
          const multiSelectSettings = hoveredColumnData.data as {
            singleSelectOptions: SelectOption[];
          };

          const selectMultiOptions = multiSelectSettings.singleSelectOptions;
          const selectedOptions = hoveredCellValueArr?.map((val) => {
            return selectMultiOptions.find((option) => option.value === val);
          });

          const countedLabels = getCountedSelectValues(selectedOptions);
          const tooltipContent = countedLabels
            .map(
              (item: { name: string; count: number }) =>
                `${item.name} x ${item.count}`,
            )
            .join(", ");

          if (countedLabels?.length) {
            setTooltipSettings({
              value: tooltipContent,
              type: hoveredColumnData.type,
              bounds: cellCoordinates.bounds,
            });
          } else {
            setTooltipSettings(initialTooltipSettings);
          }
        }

        if (hoveredColumnData.type === MetaFieldType.GlobalCreator) {
          const hoveredCellValueArr = hoveredCellMetaData.value as string[];
          const globalCreatorsData =
            globalTasksSearchResultsState.type === "done"
              ? globalTasksSearchResultsState.data
              : [];
          const selectedGlobalCreators = globalCreatorsData.filter((creator) =>
            hoveredCellValueArr.includes(creator.uuid),
          );

          if (
            selectedGlobalCreators.length >= MAX_VISIBLE_MULTI_SELECT_OPTIONS
          ) {
            setTooltipSettings({
              value: selectedGlobalCreators
                .map((item) => item.title)
                .join(", "),
              type: hoveredColumnData.type,
              bounds: cellCoordinates.bounds,
            });
          } else {
            setTooltipSettings(initialTooltipSettings);
          }
        }
      }
    },
    [
      columns,
      rowsData,
      columnsData,
      membersList,
      isFocused,
      isDropdownOpen,
      globalTasksSearchResultsState,
      selectDataSetList,
    ],
  );

  const handleRemoveValue = (cell: Item) => {
    if (hoveredCellSettings.value || dropdownSettings.value) {
      updateMetaData(cell, null);
    }
    setHoveredCellSettings((prev) => ({ ...prev, value: null }));
    setTooltipSettings(initialTooltipSettings);
  };

  const renderRemoveIcon = (readOnly: boolean, className?: string) => {
    if (readOnly) return null;
    return (
      <RemoveIcon
        className={classNames(
          "glide-data-grid__hovered-item-remove-icon",
          showSearch &&
            "glide-data-grid__hovered-item-remove-icon--transparent",
          className,
        )}
        onClick={() => {
          handleRemoveValue(hoveredCellSettings.cell);
        }}
      />
    );
  };

  const updateMetaData = useCallback(
    (cell: Item, newValue: any, customCurrencyCode?: string | null) => {
      const [col, row] = cell;

      let rowDataItems: Row[] = [];
      const columnItem = columns[col];

      if (
        selectedRowsData.length > 1 &&
        selectedRowsData.find(
          (selectedRow) => selectedRow.uuid === rowsData[row].uuid,
        )
      ) {
        rowDataItems = selectedRowsData;
      } else {
        rowDataItems.push(rowsData[row]);
      }

      let newValueLocal = newValue;

      const foundColData = columnsData.find(
        (colData) => colData.uuid === columnItem.id,
      );

      if (
        isAccessLevelReadOnly(foundColData?.accessLevel) ||
        Boolean(foundColData?.valueSource)
      )
        return;

      if (newValueLocal !== null) {
        if (foundColData?.type === MetaFieldType.Date) {
          const metaDataDateValue = newValue as DateObject;
          newValueLocal = metaDataDateValue.date;
        }
      }

      if (!columnItem.id) return;

      dispatchUpdatedMetaDataValue(
        rowDataItems,
        columnItem.id,
        newValueLocal,
        context,
        dispatch,
        customCurrencyCode,
      );
    },
    [columns, rowsData, context, selectedRowsData],
  );

  const renderHoveredCellContent = () => {
    switch (hoveredCellSettings.type) {
      case MetaFieldType.File:
        return <div className="glide-data-grid__hovered-item--only-remove" />;
      case MetaFieldType.Number:
      case MetaFieldType.Currency:
      case MetaFieldType.Percent:
        return (
          <div
            className={classNames(
              "glide-data-grid__hovered-item--remove-with-metafield",
              isScrolling && "glide-data-grid__hovered-item--non-clickable",
              hoveredCellSettings.type === MetaFieldType.Currency &&
                "glide-data-grid__hovered-item--currency",
              hoveredCellSettings.type === MetaFieldType.Currency &&
                isFocused &&
                "glide-data-grid__hovered-item--editing",
            )}
            ref={hoveredItemRef}
            onClick={() => {
              if (
                hoveredCellSettings.type === MetaFieldType.Currency &&
                !hoveredCellSettings.readOnly
              ) {
                inputRef.current?.focus();
              }
            }}
          >
            <ValueInputWrapper
              readOnly={hoveredCellSettings.readOnly}
              type={hoveredCellSettings.type}
              ref={inputRef}
              value={
                typeof hoveredCellSettings.value === "string"
                  ? hoveredCellSettings.value
                  : ""
              }
              columnCurrencyCode={hoveredCellSettings.columnCurrencyCode}
              customCurrencyCode={hoveredCellSettings.customCurrencyCode}
              customCurrencyValue={hoveredCellSettings.customCurrencyValue}
              onChange={(
                newValue: string,
                customCurrencyCode?: string | null,
              ) => {
                if (newValue.trim()) {
                  updateMetaData(
                    hoveredCellSettings.cell,
                    newValue,
                    customCurrencyCode,
                  );
                } else {
                  handleRemoveValue(hoveredCellSettings.cell);
                }
                setIsFocused(false);
              }}
              onFocus={() => {
                if (!hoveredCellSettings.readOnly) {
                  setIsFocused(true);
                }
              }}
              handleUseOnClickOutside={() => {
                setIsFocused(false);
              }}
            />
            {renderRemoveIcon(hoveredCellSettings.readOnly)}
          </div>
        );
      case MetaFieldType.Text:
        return (
          <div
            className={classNames(
              "glide-data-grid__hovered-item--remove-with-metafield",
              isScrolling && "glide-data-grid__hovered-item--non-clickable",
            )}
            ref={hoveredItemRef}
          >
            <TextBoxWrapper
              ref={inputRef}
              value={hoveredCellSettings.value}
              readOnly={hoveredCellSettings.readOnly}
              onChange={(newValue: string) => {
                if (newValue.trim()) {
                  updateMetaData(hoveredCellSettings.cell, newValue);
                } else {
                  handleRemoveValue(hoveredCellSettings.cell);
                }
                setIsFocused(false);
              }}
              onFocus={() => {
                if (!hoveredCellSettings.readOnly) {
                  setIsFocused(true);
                }
              }}
              handleUseOnClickOutside={() => {
                setIsFocused(false);
              }}
            />
            {renderRemoveIcon(hoveredCellSettings.readOnly)}
          </div>
        );
      case MetaFieldType.Rating:
        return (
          <div
            className={classNames(
              "glide-data-grid__hovered-item--remove-with-metafield",
              isScrolling && "glide-data-grid__hovered-item--non-clickable",
            )}
            ref={hoveredItemRef}
          >
            <RatingSelector
              value={parseInt(
                (hoveredCellSettings.value ?? "").toString() || "0",
                10,
              )}
              onValueChange={(newValue) =>
                updateMetaData(hoveredCellSettings.cell, newValue)
              }
            />
          </div>
        );
      case MetaFieldType.Date:
      case MetaFieldType.SingleSelect:
      case MetaFieldType.MultiSelect:
      case MetaFieldType.AggregatedSelect:
      case MetaFieldType.Member:
      case MetaFieldType.GlobalCreator:
        return (
          <div className="glide-data-grid__hovered-item--only-remove">
            {renderRemoveIcon(hoveredCellSettings.readOnly)}
          </div>
        );
      case MetaFieldType.DictionaryElement: {
        const dictionaryElementUuid =
          typeof hoveredCellSettings.value === "string"
            ? hoveredCellSettings.value
            : "";
        return (
          <div
            className={classNames(
              "glide-data-grid__hovered-item--only-remove",
              isScrolling && "glide-data-grid__hovered-item--non-clickable",
            )}
            ref={hoveredItemRef}
          >
            <NavigationIcon
              className="glide-data-grid__hovered-item-link-icon"
              onClick={(e: ReactMouseEvent<HTMLElement>) => {
                e.stopPropagation();
                openDictionaryDetails(history, dictionaryElementUuid);
              }}
            />
            {renderRemoveIcon(
              hoveredCellSettings.readOnly,
              "glide-data-grid__hovered-item-remove-icon--no-shadow",
            )}
          </div>
        );
      }
      case ExtraGlideDataGridMetaFieldTypesEnum.Title: {
        const [, row] = hoveredCellSettings.cell;
        const elementData = rowsData[row];
        return (
          <div
            ref={hoveredItemRef}
            className={classNames(
              "glide-data-grid__hovered-item--title-cell",
              {
                "glide-data-grid__hovered-item--clickable": handleRowNameClick,
                "glide-data-grid__hovered-item--with-data":
                  elementData.socialProfiles,
              },
              isScrolling && "glide-data-grid__hovered-item--non-clickable",
            )}
            onClick={() => {
              if (handleRowNameClick) {
                handleRowNameClick(rowsData[row].uuid);
              }
            }}
          >
            {context === tableDataType.CreatorDatabase && (
              <Img
                className="glide-data-grid__hovered-item-cover"
                src={getImageSource(
                  elementData?.cover,
                  "tiny",
                  creatorPlaceholder,
                )}
                fallbackImageSrc={creatorPlaceholder}
              />
            )}
            <div className="glide-data-grid__hovered-item-title-with-icons">
              <Link
                onClick={(e) => e.stopPropagation()}
                to={() => openGlideElement(elementData.uuid)}
              >
                {elementData.title.substring(0, 19)}
              </Link>
              {elementData.socialProfiles && (
                <SocialProfileIcons data={elementData.socialProfiles} />
              )}
            </div>
          </div>
        );
      }
      case MetaFieldType.Project:
        // TODO: https://app.asana.com/0/1159791777517936/1208972781734357/f

        return (
          <ProjectSelector value={null} wsWorkspaceUuid={activeWorkspaceUuid} />
        );

      default:
        return null;
    }
  };

  const renderDropdown = () => {
    switch (dropdownSettings.type) {
      case MetaFieldType.File: {
        const fileFieldColData = columnsData.find(
          (colData) => colData.uuid === columns[dropdownSettings.cell[0]].id,
        );

        return (
          <FilesDropdownWrapper
            filesData={dropdownSettings.value as MetaDataFileValue[]}
            uuid={columns[dropdownSettings.cell[0]].id as string}
            rowUuid={rowsData[dropdownSettings.cell[1]]?.uuid}
            closeDropdown={() => setIsDropdownOpen(false)}
            readOnly={dropdownSettings.readOnly}
            bounds={dropdownSettings.bounds}
            setStaticCellSettings={setStaticCellSettings}
            context={context}
            selectedRowsData={selectedRowsData}
            fieldName={fileFieldColData?.name}
            isUniqueField={fileFieldColData?.unique}
          />
        );
      }
      case MetaFieldType.Date:
        return (
          <DatePicker
            popperClassName="glide-data-grid__dropdown-date-picker"
            onChange={(newDate: any) => {
              const parsedDate = moment(newDate);
              const dateFormatted = parsedDate.format(dateFormatForBackend);
              updateMetaData(dropdownSettings.cell, {
                date: dateFormatted,
              });
              setIsDropdownOpen(false);
            }}
            open={isDropdownOpen}
            className="glide-data-grid__date-picker"
            onClickOutside={() => setIsDropdownOpen(false)}
            dateFormat={datePickerDateFormat}
            selected={
              isDateObject(dropdownSettings?.value)
                ? new Date(dropdownSettings.value.date)
                : new Date()
            }
            locale={languages[locale]}
          />
        );
      case MetaFieldType.Member:
        const foundColData = columnsData.find(
          (colData) => colData.uuid === columns[dropdownSettings.cell[0]].id,
        );

        return (
          <MembersDropdown
            uuid={columns[dropdownSettings.cell[0]].id as string}
            dataType={context}
            wsTeamUuids={foundColData?.data?.wsTeamUuids}
            onClose={() => setIsDropdownOpen(false)}
            choosePerson={(member: IMember) => {
              updateMetaData(dropdownSettings.cell, member.id);
              setIsDropdownOpen(false);
            }}
          />
        );
      case MetaFieldType.SingleSelect:
        return (
          <SelectDropdown
            mode="single"
            variant="disable_all"
            optionsData={
              dropdownSettings.options?.wsSelectDataSetUuid
                ? selectDataSetList[
                    `dataSetType_${dropdownSettings.options?.wsSelectDataSetUuid}`
                  ] || []
                : dropdownSettings.options?.singleSelectOptions
            }
            updateValue={(newValue) => {
              updateMetaData(dropdownSettings.cell, newValue);
              setHoveredCellSettings({
                ...dropdownSettings,
                style: {},
                value: newValue,
              });
              setIsDropdownOpen(false);
            }}
            uuid={columns[dropdownSettings.cell[0]].id as string}
            fieldValue={
              typeof dropdownSettings.value === "string"
                ? dropdownSettings.value
                : undefined
            }
            context={context}
            closeDropdownHanler={() => setIsDropdownOpen(false)}
          />
        );

      case MetaFieldType.MultiSelect:
        return (
          <SelectDropdown
            mode="multi"
            variant="disable_all"
            optionsData={
              dropdownSettings.options?.wsSelectDataSetUuid
                ? selectDataSetList[
                    `dataSetType_${dropdownSettings.options?.wsSelectDataSetUuid}`
                  ] || []
                : dropdownSettings.options?.singleSelectOptions
            }
            updateValue={(newValue) => {
              if (!newValue.length) {
                handleRemoveValue(dropdownSettings.cell);
              } else {
                updateMetaData(dropdownSettings.cell, newValue);
                setHoveredCellSettings({
                  ...dropdownSettings,
                  style: {},
                  value: newValue,
                });
              }
            }}
            uuid={columns[dropdownSettings.cell[0]].id as string}
            fieldValue={
              Array.isArray(dropdownSettings.value)
                ? (dropdownSettings.value as string[])
                : undefined
            }
            context={context}
            closeDropdownHanler={() => setIsDropdownOpen(false)}
          />
        );

      case MetaFieldType.AggregatedSelect:
        return (
          <AggregatedSelect
            uuid={columns[dropdownSettings.cell[0]].id as string}
            optionsData={dropdownSettings.options?.singleSelectOptions}
            fieldValue={
              Array.isArray(dropdownSettings.value)
                ? (dropdownSettings.value as string[])
                : undefined
            }
            updateValue={(newValue: any) => {
              if (!newValue.length) {
                handleRemoveValue(dropdownSettings.cell);
              } else {
                updateMetaData(dropdownSettings.cell, newValue);
                setHoveredCellSettings({
                  ...dropdownSettings,
                  style: {},
                  value: newValue,
                });
              }
            }}
            closeDropdownHandler={() => setIsDropdownOpen(false)}
            glideGridMode
          />
        );

      case MetaFieldType.DictionaryElement:
        return (
          <DictionaryElementSelector
            onValueChange={(newValue) => {
              setIsDropdownOpen(false);
              updateMetaData(dropdownSettings.cell, newValue);
            }}
            value={dropdownSettings.value}
            wsDictionaryUuid={dropdownSettings.options?.wsDictionaryUuid}
            wsDictionarySubFieldUuid={
              dropdownSettings.options?.wsDictionarySubFieldUuid
            }
            onClose={() => setIsDropdownOpen(false)}
            wsDictionaryElementsFilter={
              dropdownSettings.options?.wsDictionaryElementsFilter
            }
            rowId={dropdownSettings.rowId}
            dataType={context}
            displayOnlyMenu
          />
        );

      case MetaFieldType.GlobalCreator:
        return (
          <GlobalCreatorDropdown
            fieldValue={dropdownSettings.value as string[] | null}
            isOpen={isDropdownOpen}
            handleUpdateValue={(values: string[]) => {
              updateMetaData(dropdownSettings.cell, values);
            }}
          />
        );

      case ExtraGlideDataGridMetaFieldTypesEnum.Action: {
        const [_, row] = hoveredCellSettings.cell;
        return (
          <ActionDropdownMenu
            context={context}
            setIsDropdownOpen={setIsDropdownOpen}
            rowData={rowsData[row]}
          />
        );
      }
      case MetaFieldType.Project:
        // TODO: https://app.asana.com/0/1159791777517936/1208972781734357/f
        return null;

      default:
        return null;
    }
  };

  const resetDropdownSettings = () => {
    setDropdownSettings(initialDropdownSettings);
  };

  const onCellClicked = useCallback(
    (cell: Item, bounds: Rectangle) => {
      const [col, row] = cell;
      const clickedColumn = columns[col];
      const clickedRow = rowsData[row];
      if (!clickedColumn || !clickedRow) return;

      onItemHovered(cell, bounds);

      const basicSetup = {
        cell,
        bounds,
      };

      if (clickedColumn.id === ExtraGlideDataGridMetaFieldTypesEnum.Action) {
        setIsDropdownOpen(true);
        setDropdownSettings({
          ...basicSetup,
          rowId: "",
          type: ExtraGlideDataGridMetaFieldTypesEnum.Action,
          value: "",
          readOnly: false,
        });
        return;
      }

      const foundColData = columnsData.find(
        (colData) => colData.uuid === columns[col].id,
      );

      const foundMetaDataItem = rowsData[row].metadata.find(
        (item) => item.uuid === clickedColumn.id,
      );

      if (foundColData) {
        const readOnly =
          isAccessLevelReadOnly(foundColData.accessLevel) ||
          Boolean(foundColData.valueSource);

        // TODO: Add support of other dropdowns readOnly
        if (readOnly && foundColData.type !== MetaFieldType.File) return;
        const dropdownData = {
          rowId: rowsData[row].uuid,
          value: foundMetaDataItem?.value ?? null,
          readOnly,
        };

        if (
          foundColData.type === MetaFieldType.Date ||
          foundColData.type === MetaFieldType.Member ||
          foundColData.type === MetaFieldType.GlobalCreator ||
          (foundColData.type === MetaFieldType.File &&
            Array.isArray(foundMetaDataItem?.value))
        ) {
          setIsDropdownOpen(true);
          setDropdownSettings({
            type: foundColData.type,
            ...basicSetup,
            ...dropdownData,
          });
        } else if (
          foundColData.type === MetaFieldType.SingleSelect ||
          foundColData.type === MetaFieldType.MultiSelect ||
          foundColData.type === MetaFieldType.AggregatedSelect ||
          foundColData.type === MetaFieldType.DictionaryElement
        ) {
          setIsDropdownOpen(true);
          setDropdownSettings({
            type: foundColData.type,
            ...basicSetup,
            ...dropdownData,
            options:
              typeof foundColData.data !== "undefined" ? foundColData.data : {},
          });
        }
      }
    },
    [columns, rowsData, columnsData, isFocused],
  );

  const handleEmptyField = () => {
    if (
      hoveredCellSettings.type === MetaFieldType.Text ||
      hoveredCellSettings.type === MetaFieldType.Number ||
      hoveredCellSettings.type === MetaFieldType.Currency ||
      hoveredCellSettings.type === MetaFieldType.Percent ||
      hoveredCellSettings.type === MetaFieldType.Project
    ) {
      setHoveredCellSettings((prev) => ({ ...prev, value: "" }));
      setTimeout(() => {
        inputRef.current?.focus();
      }, 15);
    } else {
      onCellClicked(hoveredCellSettings.cell, hoveredCellSettings.bounds);
    }
  };

  const onHeaderClicked = useCallback(
    (colIndex: number) => {
      const clickedCol = columns[colIndex];
      if (
        !clickedCol ||
        !clickedCol.icon ||
        !filtersAndSortingOptions ||
        !filtersAndSortingOptionsDispatch
      )
        return;

      const newSortOption =
        clickedCol.icon === SortDirection.Ascending
          ? SortDirection.Descending
          : SortDirection.Ascending;
      // @ts-ignore
      filtersAndSortingOptionsDispatch(setDictionarySort(newSortOption));
    },
    [columns, filtersAndSortingOptions, filtersAndSortingOptionsDispatch],
  );

  const getCellContent = useCallback<DataEditorProps["getCellContent"]>(
    (cell): GridCell => {
      const generateGridCell = (
        rowItem: Row,
        colItem: GridColumn,
      ): GridCell => {
        if (colItem.id === ExtraGlideDataGridMetaFieldTypesEnum.Action) {
          return {
            kind: GridCellKind.Custom,
            cursor: "pointer",
            allowOverlay: false,
            copyData: "-",
            readonly: true,
            data: {
              kind: "button-cell",
              backgroundColor: ["#f2f2f6", "#e0e2ea"],
              color: "#6A6D7C",
              borderRadius: 8,
              title: "⋮",
            },
            themeOverride: {
              baseFontStyle: "700 16px",
            },
          } as ButtonCellType;
        }

        if (colItem.id === ExtraGlideDataGridMetaFieldTypesEnum.Title) {
          if (context === tableDataType.CreatorDatabase) {
            let coverImageUrl;

            if (isThumbnailsExist(rowItem.cover, "tiny")) {
              coverImageUrl = rowItem.cover?.thumbnails.tiny as string;
            }

            const titleCell: TitleCell = {
              kind: GridCellKind.Custom,
              allowOverlay: false,
              readonly: false,
              copyData: rowItem.title,
              data: {
                kind: "title-cell",
                name: rowItem.title,
                image: coverImageUrl,
                placeholderImage: creatorPlaceholder,
                socialProfiles: rowItem.socialProfiles,
              },
            };
            return titleCell;
          }
          return {
            kind: GridCellKind.Text,
            allowOverlay: false,
            readonly: true,
            displayData: rowItem.title || " ",
            data: rowItem.title || " ",
          };
        }

        if (colItem.id === ExtraGlideDataGridMetaFieldTypesEnum.Empty) {
          return {
            kind: GridCellKind.Text,
            allowOverlay: false,
            readonly: false,
            displayData: " ",
            data: " ",
          };
        }

        const foundMetaDataItem = rowItem.metadata.find(
          (item) => item.uuid === colItem.id,
        );

        const foundColData = columnsData.find(
          (colData) => colData.uuid === colItem.id,
        );

        if (!foundMetaDataItem || !foundColData || !foundMetaDataItem.value) {
          if (foundColData?.type === MetaFieldType.BoolVal) {
            return {
              kind: GridCellKind.Boolean,
              readonly:
                isAccessLevelReadOnly(foundColData.accessLevel) ||
                Boolean(foundColData.valueSource),
              allowOverlay: false,
              data: false,
            } as BooleanCell;
          }
          if (foundColData?.type === MetaFieldType.Rating) {
            return {
              kind: GridCellKind.Image,
              allowOverlay: false,
              rounding: 0,
              data: [`/workspace/images/star.svg`],
            } as ImageCell;
          }
          return {
            kind: GridCellKind.Text,
            allowOverlay: false,
            readonly: false,
            displayData: " ",
            data: " ",
          };
        }

        return generateSpecificGridCell(
          foundMetaDataItem.value,
          foundColData,
          membersList,
          dictionaryAutoCompletes,
          selectDataSetList,
          intl,
          globalTasksSearchResultsState.type === "done"
            ? globalTasksSearchResultsState.data
            : [],
          foundMetaDataItem.data,
        );
      };

      const [col, row] = cell;
      if (isLoading || !rowsData[row] || !columns[col]) {
        return {
          kind: GridCellKind.Loading,
          allowOverlay: false,
        };
      }
      return generateGridCell(rowsData[row], columns[col]);
    },
    [
      columns,
      rowsData,
      columnsData,
      isLoading,
      membersList,
      dictionaryAutoCompletes,
      isFocused,
      selectDataSetList,
      intl,
      context,
      globalTasksSearchResultsState,
    ],
  );

  const headerIcons = useMemo<SpriteMap>(() => headerIconsObj, []);

  const getRowThemeOverride = useCallback(
    // Hide border from extra rows that fill the missing space
    (i) => (i > rowsData.length ? { borderColor: "transparent" } : undefined),
    [rowsData],
  );

  const renderHoveredHeader = () => {
    return (
      <GlideDataGridHeader
        title={hoveredHeaderSettings.value || ""}
        bounds={hoveredHeaderSettings.bounds}
        setTooltipSettings={setTooltipSettings}
        arrowDirection={
          filtersAndSortingOptions?.sortBy === hoveredHeaderSettings.uuid
            ? filtersAndSortingOptions?.sort
            : null
        }
      />
    );
  };

  const renderHoveredCell = () => {
    if (
      hoveredCellSettings.value === null &&
      hoveredCellSettings.type &&
      hoveredCellSettings.type !== MetaFieldType.Rating
    ) {
      if (hoveredCellSettings.readOnly) return null;
      if (hoveredCellSettings.type === MetaFieldType.File) {
        const [col, row] = hoveredCellSettings.cell;
        const fileFieldColData = columnsData.find(
          (colData) => colData.uuid === columns[col].id,
        );

        return (
          <EmptyCell isScrolling={isScrolling} ref={hoveredItemRef}>
            <EmptyFileCell
              uuid={columns[col].id as string}
              rowUuid={rowsData[row].uuid}
              closeDropdown={() => setIsDropdownOpen(false)}
              bounds={hoveredCellSettings.bounds}
              setStaticCellSettings={setStaticCellSettings}
              wsWorkspaceUuid={activeWorkspaceUuid}
              context={context}
              selectedRowsData={selectedRowsData}
              fieldName={fileFieldColData?.name}
              isUniqueField={fileFieldColData?.unique}
            />
          </EmptyCell>
        );
      }
      return (
        <EmptyCell
          isScrolling={isScrolling}
          handleEmptyField={handleEmptyField}
          ref={hoveredItemRef}
        />
      );
    }
    return renderHoveredCellContent();
  };

  const clickedRowRef = useRef<number | null>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const gridElement = document.querySelector(".glide-data-grid__editor");
      if (!gridElement) return;

      // Get the scroll container
      const scrollContainer = gridElement.querySelector(".dvn-scroller");
      if (!scrollContainer) return;

      const rect = gridElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (x >= 0 && x <= DEFAULT_ROW_MARKER_WIDTH && y >= headerHeight) {
        // Get scroll offset to account for virtualization
        const scrollTop = scrollContainer.scrollTop;

        // Calculate row index including scroll offset
        const rowIndex = Math.floor(
          (y - headerHeight + scrollTop) / ROW_HEIGHT,
        );

        if (rowIndex >= 0 && rowIndex < rowsData.length) {
          clickedRowRef.current = rowIndex;
        }
      }
    };

    document.addEventListener("mousedown", handleClick, true);
    return () => {
      document.removeEventListener("mousedown", handleClick, true);
    };
  }, [rowsData.length, headerHeight]);

  const handleGridSelectionChange = useCallback(
    (_newSelection: GridSelection) => {
      // Prevents unselecting rows when clicking on a cell
      if (_newSelection.current) return;

      const selectedRows = _newSelection.rows.toArray();
      const previousSelectedRows = selection?.rows.toArray() || [];

      const isShiftPressed = isShiftPressedRef.current;

      const rowDifference = Math.abs(
        selectedRows.length - previousSelectedRows.length,
      );

      if (
        isShiftPressed &&
        lastClickedRowIndex !== null &&
        clickedRowRef.current !== null
      ) {
        const clickedRowIndex = clickedRowRef.current;
        clickedRowRef.current = null;

        if (clickedRowIndex !== null) {
          const isClickedRowCurrentlySelected =
            previousSelectedRows.includes(clickedRowIndex);

          // Create the range between last clicked and current clicked
          const start = Math.min(lastClickedRowIndex, clickedRowIndex);
          const end = Math.max(lastClickedRowIndex, clickedRowIndex);
          const rangeRows: number[] = [];
          for (let i = start; i <= end; i++) {
            rangeRows.push(i);
          }

          let updatedSelection: number[];
          if (isClickedRowCurrentlySelected) {
            updatedSelection = previousSelectedRows.filter(
              (row) => !rangeRows.includes(row),
            );
          } else {
            updatedSelection = Array.from(
              new Set([...previousSelectedRows, ...rangeRows]),
            );
          }

          setSelectedRowsData(
            updatedSelection.map((selectedRow) => rowsData[selectedRow]),
          );

          setSelection({
            ..._newSelection,
            rows: addNewSelectedRows(updatedSelection),
          });

          setLastClickedRowIndex(clickedRowIndex);
          return;
        }
      }

      if (rowDifference === 1) {
        const newRow = selectedRows.find(
          (row) => !previousSelectedRows.includes(row),
        );
        const removedRow = previousSelectedRows.find(
          (row) => !selectedRows.includes(row),
        );
        const clickedRow = newRow ?? removedRow;
        if (clickedRow !== undefined) {
          setLastClickedRowIndex(clickedRow);
        }
      } else if (
        selectedRows.length === 1 &&
        previousSelectedRows.length === 0
      ) {
        setLastClickedRowIndex(selectedRows[0]);
      } else if (selectedRows.length === 0) {
        setLastClickedRowIndex(null);
      }

      setSelectedRowsData(
        selectedRows.map((selectedRow) => rowsData[selectedRow]),
      );

      setSelection(_newSelection);
    },
    [rowsData, setSelection, selection, lastClickedRowIndex],
  );

  const onColumnResize = useCallback(
    (col: GridColumn, newSize: number) => {
      const index = columns.findIndex((c) => c.id === col.id);

      const newCols = [...columns];

      if (newCols[index] !== undefined && newCols[index].id === "action")
        return;

      newCols[index] = {
        ...newCols[index],
        width: newSize,
      };
      setColumns(newCols);
    },
    [columns],
  );

  useEffect(() => {
    const generatedColumns = generateColumns(
      columnsData,
      totalRowsCount,
      rowsData.length,
      filtersAndSortingOptions,
    );
    setColumns(generatedColumns);
  }, [columnsData, filtersAndSortingOptions, totalRowsCount, rowsData.length]);

  const editor = document.querySelector(".dvn-scroller");

  useEffect(() => {
    if (editor) {
      editor.addEventListener("scroll", () => {
        setHoveredCellSettings(initialHoveredCellSettings);
        setHoveredHeaderSettings(initialHoveredHeaderSettings);
        setTooltipSettings(initialTooltipSettings);
        setIsDropdownOpen(false);
        setIsFocused(false);
      });
      return () => {
        editor.removeEventListener("scroll", () => {
          setHoveredCellSettings(initialHoveredCellSettings);
          setHoveredHeaderSettings(initialHoveredHeaderSettings);
          setTooltipSettings(initialTooltipSettings);
        });
      };
    }
  }, [editor]);

  useEffect(() => {
    if (document) {
      document.documentElement.style.overflow = "hidden";
      return () => {
        document.documentElement.style.overflow = "";
      };
    }
  }, []);

  useEffect(() => {
    if (isDropdownOpen) {
      setTooltipSettings(initialTooltipSettings);
    } else {
      resetDropdownSettings();
    }
  }, [isDropdownOpen]);

  const cols = JSON.stringify(
    columnsData.map((column) => ({
      type: column.type,
      options: Array.isArray(column.data)
        ? []
        : {
            singleSelectOptions: column.data.singleSelectOptions?.sort((a, b) =>
              a.name.localeCompare(b.name),
            ),
            ...column.data,
          },
    })),
  );

  useEffect(() => {
    getDictionaryAutoCompletesForColumns(dispatch, JSON.parse(cols));
    getSelectDataSetForColumns(dispatch, JSON.parse(cols));
  }, [cols]);

  useEffect(() => {
    const hoveredElement = hoveredItemRef.current;
    if (!hoveredElement || isFocused) return;

    const handleScroll = () => {
      setIsScrolling(true);

      setTimeout(() => {
        setIsScrolling(false);
      });
    };

    hoveredElement.addEventListener("wheel", handleScroll);
    return () => {
      hoveredElement.removeEventListener("wheel", handleScroll);
    };
  }, [isFocused]);

  useEffect(() => {
    if (showSearch) {
      const searchContainer = document.querySelector(".gdg-seveqep");
      if (searchContainer) {
        const hideElements = () => {
          setHoveredCellSettings(initialHoveredCellSettings);
          setTooltipSettings(initialTooltipSettings);
          setHoveredHeaderSettings(initialHoveredHeaderSettings);
        };

        searchContainer.addEventListener("mouseenter", hideElements);
        return () => {
          searchContainer.removeEventListener("mouseenter", hideElements);
        };
      }
    }
  }, [showSearch]);

  useEffect(() => {
    if (!selection || !selectedRowsData.length) return;

    if (selection.rows.length === selectedRowsData.length) {
      const selectedIndexesFromRowsData = selectedRowsData.map(
        (selectedRowData) => {
          return rowsData.findIndex(
            (rowData) => rowData.uuid === selectedRowData.uuid,
          );
        },
      );
      if (
        areArraysTheSame(selection.rows.toArray(), selectedIndexesFromRowsData)
      )
        return;

      setSelection((prev) => {
        if (prev) {
          return {
            ...prev,
            rows: addNewSelectedRows(selectedIndexesFromRowsData),
          };
        }
      });
    }
  }, [selection, rowsData, selectedRowsData]);

  useEffect(() => {
    if (!selection) {
      setSelectedRowsData([]);
    }
  }, [selection]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        isShiftPressedRef.current = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        isShiftPressedRef.current = false;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  useEffect(() => {
    const globalCreatorFieldUuids: string[] = [];

    columnsData.forEach((columnData) => {
      if (columnData.type === MetaFieldType.GlobalCreator) {
        globalCreatorFieldUuids.push(columnData.uuid);
      }
    });

    const globalCreatorUuids: string[] = [];
    rowsData.forEach((rowData) => {
      const foundGlobalCreatorMetaData = rowData.metadata.find((item) =>
        globalCreatorFieldUuids.includes(item.uuid),
      );
      if (
        foundGlobalCreatorMetaData &&
        Array.isArray(foundGlobalCreatorMetaData.value)
      ) {
        foundGlobalCreatorMetaData.value.forEach((value) => {
          if (
            typeof value === "string" &&
            !globalCreatorUuids.includes(value)
          ) {
            globalCreatorUuids.push(value);
          }
        });
      }
    });

    if (globalCreatorUuids.length > 0) {
      initializeGlobalTasksSearchResultsFetch(
        activeWorkspaceUuid,
        globalCreatorUuids,
      );
    }
  }, [
    columnsData,
    rowsData,
    initializeGlobalTasksSearchResultsFetch,
    activeWorkspaceUuid,
  ]);

  return (
    <>
      <div className="glide-data-grid__line" />
      <DataEditor
        customRenderers={[...allCells, creatorCellRenderer]}
        getCellContent={getCellContent}
        columns={columns}
        rows={rowsData.length}
        smoothScrollX
        smoothScrollY
        freezeColumns={FROZEN_COLUMN_COUNT}
        theme={initialTheme}
        rowHeight={ROW_HEIGHT}
        headerHeight={headerHeight}
        drawHeader={drawHeader}
        drawCell={drawCell}
        onColumnResize={
          context === tableDataType.Dictionary ? onColumnResize : undefined
        }
        className="glide-data-grid__editor"
        onItemHovered={(args) => {
          if (args.kind === "header") {
            onHeaderHovered(args.location, args.bounds);
          } else if (hoveredHeaderSettings.value) {
            setHoveredHeaderSettings(initialHoveredHeaderSettings);
          }

          if (
            args.kind === "cell" &&
            willElementRenderInsideHoverArea(editor, args.bounds)
          ) {
            onItemHovered(args.location, args.bounds);
          } else {
            if (!isFocused && !isDropdownOpen) {
              setHoveredCellSettings(initialHoveredCellSettings);
            }
            if (args.kind === "out-of-bounds") {
              setTooltipSettings(initialTooltipSettings);
            }
          }
        }}
        getCellsForSelection
        columnSelect="none"
        rowSelectionMode="multi"
        rowMarkers={rowMarkersSettings}
        getRowThemeOverride={getRowThemeOverride}
        headerIcons={headerIcons}
        showSearch={showSearch}
        drawFocusRing={false}
        onSearchClose={() => setShowSearch(false)}
        onCellClicked={(cell, event) => onCellClicked(cell, event.bounds)}
        onCellEdited={(cell, newValue) => updateMetaData(cell, newValue.data)}
        onHeaderClicked={onHeaderClicked}
        onGridSelectionChange={handleGridSelectionChange}
        gridSelection={selection}
      />

      {hoveredHeaderSettings.value && (
        <div
          className="glide-data-grid__hovered-header"
          ref={setHeaderPopperRef}
          style={{
            ...hoveredHeaderSettings.style,
          }}
        >
          {renderHoveredHeader()}
        </div>
      )}

      {hoveredCellSettings.type && (
        <div
          className="glide-data-grid__hovered-item"
          ref={setCellPopperRef}
          style={{
            ...hoveredCellSettings.style,
          }}
        >
          {renderHoveredCell()}
        </div>
      )}

      {staticCellSettings.numberOfFiles > 0 && (
        <StaticFilesLoadingCell settings={staticCellSettings} />
      )}

      {tooltipSettings.value &&
        (tooltipSettings.type === MetaFieldType.MultiSelect ||
          tooltipSettings.type === MetaFieldType.AggregatedSelect ||
          tooltipSettings.type === MetaFieldType.Member ||
          tooltipSettings.type === MetaFieldType.GlobalCreator ||
          tooltipSettings.type === "header") && (
          <GlideDataGridTooltip
            content={tooltipSettings.value}
            bounds={tooltipSettings.bounds}
            type={tooltipSettings.type}
            referenceElement={
              tooltipSettings.type === "header"
                ? headerPopperRef
                : cellPopperRef
            }
          />
        )}

      {isDropdownOpen && (
        <GlideDataGridDropdownWrapper
          handleClickOverlay={
            dropdownSettings.type !== MetaFieldType.SingleSelect &&
            dropdownSettings.type !== MetaFieldType.MultiSelect &&
            dropdownSettings.type !== MetaFieldType.AggregatedSelect
              ? () => setIsDropdownOpen(false)
              : undefined
          }
          referenceElement={cellPopperRef}
          cellBounds={dropdownSettings.bounds}
        >
          {renderDropdown()}
        </GlideDataGridDropdownWrapper>
      )}
      {selectedRowsData.length > 0 && (
        <GlideDataGridSelectedRowsBar
          selectedRowsData={selectedRowsData}
          context={context}
          setSelection={setSelection}
        />
      )}
    </>
  );
}

export default GlideDataGrid;
